import React from 'react';
import styled from 'styled-components';
import { 
  FileText, 
  Users, 
  Search, 
  MessageSquare, 
  CheckCircle,
  Scale,
  Shield,
  Clock,
  ChevronRight,
  HelpCircle
} from 'lucide-react';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
`;

const IntroCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);
  margin-bottom: 2rem;
  text-align: center;

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.75rem;
    color: var(--primary-blue);
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: var(--text-medium);
    line-height: 1.8;
    max-width: 800px;
    margin: 0 auto;
  }
`;

const StepsSection = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.75rem;
  color: var(--text-dark);
  margin-bottom: 2rem;
  text-align: center;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  text-align: center;
  position: relative;
  transition: var(--transition);

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-gold);
  }

  .number {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, var(--accent-gold), #ffd940);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-family: var(--font-secondary);
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--primary-blue-dark);
  }

  .icon {
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, var(--primary-blue), #004d8f);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: var(--white);
  }

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: var(--text-medium);
    line-height: 1.6;
  }

  .duration {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 1rem;
    padding: 0.4rem 1rem;
    background: rgba(0, 38, 100, 0.05);
    border-radius: var(--radius-full);
    font-size: 0.85rem;
    color: var(--primary-blue);
    font-weight: 600;
  }
`;

const ProcessSection = styled.div`
  margin-bottom: 3rem;
`;

const ProcessCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  overflow: hidden;
`;

const ProcessHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  padding: 1.5rem 2rem;
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 40px;
    height: 40px;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
    padding: 0.5rem;
  }

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
  }
`;

const ProcessList = styled.div`
  padding: 1.5rem 2rem;
`;

const ProcessItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }

  .icon {
    width: 50px;
    height: 50px;
    background: var(--background-light);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-blue);
    flex-shrink: 0;
  }

  .content {
    flex: 1;

    h4 {
      font-size: 1.1rem;
      font-weight: 700;
      color: var(--text-dark);
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.95rem;
      color: var(--text-medium);
      line-height: 1.6;
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
    color: var(--primary-blue);
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg {
      color: var(--accent-gold);
    }
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    font-size: 0.95rem;
    color: var(--text-dark);
    line-height: 1.5;
  }
`;



const Methodologie = () => {
  return (
    (<PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Méthodologie</HeroTitle>
          <HeroSubtitle>
                      Découvrez comment la Médiature traite votre Réclamation étape par étape
                    </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <ContentWrapper>
          <IntroCard>
            <h2>Notre engagement</h2>
            <p>
                          La Médiature de la République suit une méthodologie rigoureuse et transparente 
                          pour traiter chaque Réclamation. Notre objectif est de résoudre les litiges de manière 
                          équitable, rapide et dans le respect des droits de chacun.
                        </p>
          </IntroCard>

          <StepsSection>
            <SectionTitle>Les 4 étapes du processus</SectionTitle>
            <StepsGrid>
              <StepCard>
                <div className="icon">
                  <FileText size={32} />
                </div>
                <h3>1. Réception</h3>
                <p>Votre Réclamation est enregistrée et un accusé de réception vous est envoyé avec un numéro de référence unique.</p>
                <div className="duration">
                  <Clock size={14} />
                  24 heures
                </div>
              </StepCard>
              <StepCard>
                <div className="icon">
                  <Search size={32} />
                </div>
                <h3>2. Analyse</h3>
                <p>Notre équipe analyse votre dossier pour vérifier la recevabilité et déterminer les actions à entreprendre.</p>
                <div className="duration">
                  <Clock size={14} />
                  7 jours
                </div>
              </StepCard>
              <StepCard>
                <div className="icon">
                  <MessageSquare size={32} />
                </div>
                <h3>3. Enquête</h3>
                <p>Nous contactons l'administration concernée et recueillons tous les éléments nécessaires à la compréhension du litige.</p>
                <div className="duration">
                  <Clock size={14} />
                  15 jours
                </div>
              </StepCard>
              <StepCard>
                <div className="icon">
                  <Scale size={32} />
                </div>
                <h3>4. Résolution</h3>
                <p>Une recommandation est émise et communiquée aux parties. Un suivi est assuré pour garantir l'application.</p>
                <div className="duration">
                  <Clock size={14} />
                  30 jours max
                </div>
              </StepCard>
            </StepsGrid>
          </StepsSection>

          <ProcessSection>
            <ProcessCard>
              <ProcessHeader>
                <CheckCircle size={24} />
                <h3>Critères de recevabilité</h3>
              </ProcessHeader>
              <ProcessList>
                <ProcessItem>
                  <div className="icon">
                    <Shield size={24} />
                  </div>
                  <div className="content">
                    <h4>Compétence de la Médiature</h4>
                    <p>La Réclamation doit concerner une administration publique, un établissement public ou un organisme de service public.</p>
                  </div>
                </ProcessItem>
                <ProcessItem>
                  <div className="icon">
                    <Users size={24} />
                  </div>
                  <div className="content">
                    <h4>Qualité du requérant</h4>
                    <p>Toute personne physique ou morale peut saisir la Médiature, y compris par l'intermédiaire d'un tiers.</p>
                  </div>
                </ProcessItem>
                <ProcessItem>
                  <div className="icon">
                    <Clock size={24} />
                  </div>
                  <div className="content">
                    <h4>Délai de saisine</h4>
                    <p>La Réclamation doit être déposée dans un délai d'un an à compter des faits. Passé ce délai, elle est irrecevable.</p>
                  </div>
                </ProcessItem>
                <ProcessItem>
                  <div className="icon">
                    <HelpCircle size={24} />
                  </div>
                  <div className="content">
                    <h4>Épuisement des voies de recours</h4>
                    <p>En principe, les voies de recours administratifs doivent être épuisées avant de saisir la Médiature.</p>
                  </div>
                </ProcessItem>
              </ProcessList>
            </ProcessCard>
          </ProcessSection>

          <InfoGrid>
            <InfoCard>
              <h3><Scale size={24} /> Principes directeurs</h3>
              <ListItem>
                <ChevronRight size={18} />
                <span><strong>Gratuité :</strong> Le service est entièrement gratuit pour les usagers</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span><strong>Confidentialité :</strong> Vos informations sont strictement protégées</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span><strong>Impartialité :</strong> Traitement neutre et objectif de chaque dossier</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span><strong>Transparence :</strong> Information régulière sur l'avancement</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span><strong>Rapidité :</strong> Délai moyen de traitement de 30 jours</span>
              </ListItem>
            </InfoCard>

            <InfoCard>
              <h3><CheckCircle size={24} /> Nos engagements</h3>
              <ListItem>
                <ChevronRight size={18} />
                <span>Réponse sous 24h après dépôt de la Réclamation</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span>Accusé de réception avec numéro de suivi</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span>Information régulière sur l'avancement</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span>Recommandation motivée et constructive</span>
              </ListItem>
              <ListItem>
                <ChevronRight size={18} />
                <span>Suivi post-recommandation</span>
              </ListItem>
            </InfoCard>
          </InfoGrid>


        </ContentWrapper>
      </Container>
    </PageContainer>)
  );
};

export default Methodologie;

